import type { ReactionDefinition, ReactionKind, LiveReaction, TimeOfDayScene } from "./types";

export const REACTIONS: ReactionDefinition[] = [
  { kind: "sun", label: "Happy sun", glyph: "☀️", lane: "sky", durationMs: 30000 },
  { kind: "moon", label: "Winking moon", glyph: "🌙", lane: "sky", durationMs: 30000 },
  { kind: "shootingStar", label: "Shooting star", glyph: "✦", lane: "sky", durationMs: 30000 },
  { kind: "seagull", label: "Seagull fly-by", glyph: "〰", lane: "sky", durationMs: 30000 },
  { kind: "cocktail", label: "Beach toast", glyph: "🍹", lane: "sand", durationMs: 30000 },
  { kind: "scotch", label: "Scotch rocks", glyph: "🥃", lane: "sand", durationMs: 30000 },
  { kind: "heart", label: "Love Stone Harbor", glyph: "❤️", lane: "sky", durationMs: 30000 },
  { kind: "fireworks", label: "Fireworks", glyph: "🎆", lane: "burst", durationMs: 30000 },
  { kind: "sandcastle", label: "Sandcastle", glyph: "🏰", lane: "sand", durationMs: 30000 },
  { kind: "surferGirl", label: "Surfer girl", glyph: "🏄‍♀️", lane: "water", durationMs: 30000 },
  { kind: "surferBoy", label: "Surfer boy", glyph: "🏄‍♂️", lane: "water", durationMs: 30000 },
  { kind: "basset", label: "Basset hound", glyph: "🐶", lane: "sand", durationMs: 30000 },
  { kind: "rabbit", label: "Rabbit", glyph: "🐰", lane: "sand", durationMs: 30000 },
  { kind: "lifeboat", label: "Stone Harbor lifeboat", glyph: "SH", lane: "water", durationMs: 30000 },
  { kind: "kite", label: "Kite", glyph: "🪁", lane: "sky", durationMs: 30000 },
  { kind: "biplane", label: "Old biplane", glyph: "🛩️", lane: "sky", durationMs: 30000 },
  { kind: "hotAirBalloon", label: "Hot air balloon", glyph: "🎈", lane: "sky", durationMs: 30000 },
  { kind: "dolphin", label: "Dolphin", glyph: "🐬", lane: "water", durationMs: 30000 },
  { kind: "sandpiper", label: "Sandpiper", glyph: "🐦", lane: "sand", durationMs: 30000 },
  { kind: "crab", label: "Crab", glyph: "🦀", lane: "sand", durationMs: 30000 },
  { kind: "umbrella", label: "Beach umbrella", glyph: "⛱️", lane: "sand", durationMs: 30000 },
  { kind: "shell", label: "Sea shell", glyph: "🐚", lane: "sand", durationMs: 30000 }
];

export const REACTION_BY_KIND = new Map<ReactionKind, ReactionDefinition>(
  REACTIONS.map((reaction) => [reaction.kind, reaction])
);

export function isShootingStarAvailable(timeOfDay: TimeOfDayScene): boolean {
  return timeOfDay === "night";
}

export function createReaction(kind: ReactionKind, now = new Date()): LiveReaction {
  const definition = REACTION_BY_KIND.get(kind);
  if (!definition) {
    throw new Error(`Unknown reaction kind: ${kind}`);
  }

  const expiresAt = new Date(now.getTime() + definition.durationMs);
  const placement = kind === "shootingStar" ? randomShootingStarPlacement() : randomPlacement(definition.lane);
  return {
    id: crypto.randomUUID(),
    kind,
    label: definition.label,
    glyph: definition.glyph,
    lane: definition.lane,
    createdAt: now.toISOString(),
    expiresAt: expiresAt.toISOString(),
    ...placement
  };
}

function randomPlacement(lane: ReactionDefinition["lane"]): Pick<LiveReaction, "x" | "y"> {
  if (lane === "water") {
    return { x: Math.round(10 + Math.random() * 78), y: Math.round(72 + Math.random() * 9) };
  }
  if (lane === "sand") {
    return { x: Math.round(10 + Math.random() * 78), y: Math.round(78 + Math.random() * 10) };
  }
  if (lane === "burst") {
    return { x: Math.round(18 + Math.random() * 64), y: Math.round(20 + Math.random() * 18) };
  }
  return { x: Math.round(12 + Math.random() * 76), y: Math.round(10 + Math.random() * 32) };
}

function randomShootingStarPlacement(): Pick<LiveReaction, "x" | "y" | "shootingStar"> {
  const routes = [
    () => ({ x: -18, y: randomBetween(8, 29), dx: randomBetween(610, 690), dy: randomBetween(115, 205) }),
    () => ({ x: -16, y: randomBetween(38, 52), dx: randomBetween(600, 680), dy: -randomBetween(105, 175) }),
    () => ({ x: 112, y: randomBetween(8, 30), dx: -randomBetween(610, 700), dy: randomBetween(120, 210) }),
    () => ({ x: 110, y: randomBetween(38, 54), dx: -randomBetween(600, 690), dy: -randomBetween(95, 170) }),
    () => ({ x: randomBetween(8, 32), y: -13, dx: randomBetween(285, 420), dy: randomBetween(285, 365) }),
    () => ({ x: randomBetween(64, 92), y: -13, dx: -randomBetween(285, 420), dy: randomBetween(285, 365) })
  ];
  const route = routes[Math.floor(Math.random() * routes.length)]();
  return {
    x: route.x,
    y: route.y,
    shootingStar: {
      dx: route.dx,
      dy: route.dy,
      angle: Math.round((Math.atan2(route.dy, route.dx) * 180) / Math.PI)
    }
  };
}

function randomBetween(min: number, max: number): number {
  return Math.round(min + Math.random() * (max - min));
}
